import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { ArrowUpRight, Loader2, MessagesSquare, Monitor, Search, SearchX, X } from "lucide-react"
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { useComputers } from "../../computers/context"
import { FederatedTaskSearch, SEARCH_RESULTS_PER_COMPUTER, type FederatedTaskSearchResult, type TaskSearchMatch } from "../../computers/taskSearch"
import { HARNESS_IDS, HARNESS_META } from "../../modelRegistry"
import type { HarnessId } from "../../providerEvents/harnessEventTypes"
import { codeRoutes } from "../../routing"
import { Modal } from "../ui/Modal"

const SEARCH_DEBOUNCE_MS = 180
const INDEXING_REFRESH_MS = 1_000
const LIVE_REFRESH_MS = 5_000
const exactDateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" })
const relativeDateFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: "always" })
type HarnessFilter = HarnessId | "all"

const relativeTimeUnits: Array<{ unit: Intl.RelativeTimeFormatUnit; duration: number; limit: number }> = [
    { unit: "minute", duration: 60_000, limit: 60 },
    { unit: "hour", duration: 60 * 60_000, limit: 24 },
    { unit: "day", duration: 24 * 60 * 60_000, limit: 7 },
    { unit: "week", duration: 7 * 24 * 60 * 60_000, limit: 5 },
    { unit: "month", duration: 30 * 24 * 60 * 60_000, limit: 12 },
    { unit: "year", duration: 365 * 24 * 60 * 60_000, limit: Number.POSITIVE_INFINITY },
]

function resultTime(value: string | undefined): { label: string; exact: string } | undefined {
    if (!value) return undefined
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return undefined
    const difference = date.getTime() - Date.now()
    if (Math.abs(difference) < 60_000) return { label: "just now", exact: exactDateFormatter.format(date) }
    const relative = relativeTimeUnits.find(({ duration, limit }) => Math.abs(difference) < duration * limit) ?? relativeTimeUnits.at(-1)
    if (!relative) return undefined
    return {
        label: relativeDateFormatter.format(Math.round(difference / relative.duration), relative.unit),
        exact: exactDateFormatter.format(date),
    }
}

function readablePreview(value: string): string {
    return value
        .replace(/<\/?[a-z][^>]*>/gi, " ")
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/(^|\s)#{1,6}\s*/g, "$1")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/__([^_]+)__/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/\s+/g, " ")
        .trim()
}

function highlightedPreview(text: string, query: string): ReactNode {
    const terms = query
        .trim()
        .split(/[^\p{L}\p{N}_]+/u)
        .filter(Boolean)
        .sort((left, right) => right.length - left.length)
    if (terms.length === 0) return text
    const pattern = new RegExp(`(?<![\\p{L}\\p{N}_])(${terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "giu")
    return text.split(pattern).map((part, index) => {
        const matches = terms.some((term) => part.toLocaleLowerCase().startsWith(term.toLocaleLowerCase()))
        return matches ? (
            <mark key={`${part}.${index}`} className="bg-primary/15 text-base-content">
                {part}
            </mark>
        ) : (
            part
        )
    })
}

function moveResultFocus(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Home" && event.key !== "End") return
    const results = Array.from(event.currentTarget.closest('[role="dialog"]')?.querySelectorAll<HTMLButtonElement>("[data-task-search-result]") ?? [])
    if (results.length === 0) return
    const currentIndex = results.findIndex((result) => result === event.currentTarget)
    const nextIndex =
        event.key === "Home"
            ? 0
            : event.key === "End"
              ? results.length - 1
              : currentIndex === -1
                ? event.key === "ArrowDown"
                    ? 0
                    : results.length - 1
                : (currentIndex + (event.key === "ArrowDown" ? 1 : -1) + results.length) % results.length
    event.preventDefault()
    results[nextIndex]?.focus()
}

function SearchResultCard({ match, query, onOpen }: { match: TaskSearchMatch; query: string; onOpen: () => void }) {
    const preview = match.preview ? readablePreview(match.preview) : undefined
    const time = resultTime(match.updatedAt)
    const messageLabel = `${match.messageMatchCount} matching ${match.messageMatchCount === 1 ? "message" : "messages"}`
    const matchLabel = match.kind === "title" ? (match.messageMatchCount > 0 ? `Title + ${messageLabel}` : "Title match") : messageLabel
    const harnessLabel = match.harnessIds.map((harnessId) => HARNESS_META[harnessId].name).join(", ")

    return (
        <button
            type="button"
            data-task-search-result="true"
            className="btn group grid min-h-24 w-full min-w-0 grid-cols-[2rem_minmax(0,1fr)_1.25rem] items-start gap-3 border border-border bg-base-200/35 px-3 py-3 text-left text-base-content transition-colors hover:border-primary/35 hover:bg-base-200/70 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
            onClick={onOpen}
            onKeyDown={moveResultFocus}
        >
            <span className="flex h-8 w-8 items-center justify-center border border-border bg-base-100 text-muted" aria-hidden="true">
                <MessagesSquare className="h-4 w-4" />
            </span>
            <span className="min-w-0">
                <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-5 text-base-content">{match.title}</span>
                    <span className="flex-none text-[10px] font-medium uppercase tracking-[0.08em] text-muted">{match.projectName}</span>
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-muted">
                    {harnessLabel ? <span>{harnessLabel}</span> : null}
                    {harnessLabel ? <span aria-hidden="true">·</span> : null}
                    <span>{matchLabel}</span>
                    {time ? (
                        <>
                            <span aria-hidden="true">·</span>
                            <time
                                dateTime={match.updatedAt}
                                title={time.exact}
                                aria-label={`Updated ${time.label}; ${time.exact}`}
                                className="font-medium text-base-content/75"
                            >
                                Updated {time.label}
                            </time>
                        </>
                    ) : null}
                </span>
                {preview ? (
                    <span className="mt-2 block border-l-2 border-primary/30 pl-3 text-[13px] leading-5 text-base-content/80">
                        <span className="line-clamp-3">{highlightedPreview(preview, query)}</span>
                    </span>
                ) : (
                    <span className="mt-2 block text-xs leading-5 text-muted">The task title matched your search.</span>
                )}
            </span>
            <ArrowUpRight className="mt-1 h-4 w-4 text-muted/50 transition-colors group-hover:text-primary" aria-hidden="true" />
        </button>
    )
}

export const TaskSearchModal = NiceModal.create(() => {
    const modal = useModal()
    const navigate = useNavigate()
    const computers = useComputers()
    const [query, setQuery] = useState("")
    const [harnessFilter, setHarnessFilter] = useState<HarnessFilter>("all")
    const [result, setResult] = useState<FederatedTaskSearchResult | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string>()
    const requestRef = useRef<{ id: number; controller: AbortController }>({ id: 0, controller: new AbortController() })
    const search = useMemo(() => new FederatedTaskSearch(computers.backend, computers.state.computers), [computers.backend, computers.state.computers])

    const runSearch = useCallback(
        async (value: string) => {
            const normalized = value.trim()
            if (normalized.length < 2) return
            requestRef.current.controller.abort()
            const request = { id: requestRef.current.id + 1, controller: new AbortController() }
            requestRef.current = request
            setLoading(true)
            setError(undefined)
            try {
                const next = await search.search(normalized, request.controller.signal)
                if (requestRef.current.id === request.id) setResult(next)
            } catch (searchError) {
                if (!request.controller.signal.aborted && requestRef.current.id === request.id) {
                    setError(searchError instanceof Error ? searchError.message : String(searchError))
                }
            } finally {
                if (requestRef.current.id === request.id) setLoading(false)
            }
        },
        [search]
    )

    useEffect(() => {
        const normalized = query.trim()
        if (normalized.length < 2) {
            requestRef.current.controller.abort()
            setResult(null)
            setLoading(false)
            setError(undefined)
            return
        }
        const timeout = globalThis.setTimeout(() => void runSearch(normalized), SEARCH_DEBOUNCE_MS)
        return () => globalThis.clearTimeout(timeout)
    }, [query, runSearch])

    useEffect(() => {
        if (loading || !result || query.trim().length < 2) return
        const indexing = result.computers.some((computer) => computer.refresh?.state === "indexing")
        const timeout = globalThis.setTimeout(() => void runSearch(query), indexing ? INDEXING_REFRESH_MS : LIVE_REFRESH_MS)
        return () => globalThis.clearTimeout(timeout)
    }, [loading, query, result, runSearch])

    useEffect(
        () => () => {
            requestRef.current.controller.abort()
            search.dispose()
        },
        [search]
    )

    const openMatch = async (nodeId: string, match: TaskSearchMatch) => {
        if (!(await computers.selectScope(nodeId))) return
        navigate(codeRoutes.CodeWorkspaceTask.makePath({ workspaceId: match.workspaceId, taskId: match.taskId }), { replace: true })
        modal.remove()
    }

    const filteredComputers =
        result?.computers.map((computer) => ({
            ...computer,
            matches: (harnessFilter === "all" ? computer.matches : computer.matches.filter((match) => match.harnessIds.includes(harnessFilter))).slice(
                0,
                SEARCH_RESULTS_PER_COMPUTER
            ),
        })) ?? []
    const totalMatches = filteredComputers.reduce((total, computer) => total + computer.matches.length, 0)
    const indexing = result?.computers.some((computer) => computer.refresh?.state === "indexing") ?? false
    const visibleComputers = filteredComputers.filter(
        (computer) => computer.matches.length > 0 || Boolean(computer.error) || Boolean(computer.warning) || computer.refresh?.state === "indexing"
    )

    return (
        <Modal title="Search tasks" size="lg">
            <div className="flex min-h-[28rem] flex-col gap-3">
                <label className="flex h-12 items-center gap-2 border border-border bg-input px-3 transition-colors focus-within:border-primary/60 focus-within:ring-1 focus-within:ring-primary/20">
                    <Search className="h-4 w-4 flex-none text-muted" />
                    <input
                        autoFocus
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={moveResultFocus}
                        placeholder="Search task titles and messages"
                        aria-label="Search task titles and messages"
                        className="input min-w-0 flex-1 !bg-transparent !px-0 text-sm text-base-content outline-none placeholder:text-muted/70 [&::-webkit-search-cancel-button]:hidden"
                    />
                    {loading ? <Loader2 className="h-4 w-4 flex-none animate-spin text-muted" aria-label="Searching" /> : null}
                    {query ? (
                        <button
                            type="button"
                            className="btn flex h-7 w-7 flex-none items-center justify-center bg-transparent text-muted transition-colors hover:bg-base-200 hover:text-base-content focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary/50"
                            onClick={() => setQuery("")}
                            aria-label="Clear search"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                </label>

                <div role="group" aria-label="Filter by harness" className="flex flex-wrap items-center gap-1.5 px-0.5">
                    <span className="mr-1 text-[10px] font-medium uppercase tracking-[0.08em] text-muted">Harness</span>
                    {(["all", ...HARNESS_IDS] as const).map((harnessId) => {
                        const selected = harnessFilter === harnessId
                        return (
                            <button
                                key={harnessId}
                                type="button"
                                aria-pressed={selected}
                                className={`btn h-7 border px-2.5 text-xs transition-colors ${
                                    selected
                                        ? "border-primary/40 bg-primary/10 text-base-content"
                                        : "border-border bg-transparent text-muted hover:bg-base-200 hover:text-base-content"
                                }`}
                                onClick={() => setHarnessFilter(harnessId)}
                                onKeyDown={moveResultFocus}
                            >
                                {harnessId === "all" ? "All" : HARNESS_META[harnessId].name}
                            </button>
                        )
                    })}
                </div>

                {error ? (
                    <div role="alert" className="border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
                        {error}
                    </div>
                ) : null}

                {query.trim().length < 2 ? (
                    <div className="flex flex-1 items-center justify-center px-4 py-12 text-center">
                        <div className="max-w-sm">
                            <Search className="mx-auto h-7 w-7 text-muted/60" aria-hidden="true" />
                            <div className="mt-3 text-sm font-medium text-base-content">Search every task</div>
                            <p className="mt-1 text-xs leading-5 text-muted">
                                Find words from task titles, your messages, and assistant replies across connected computers.
                            </p>
                        </div>
                    </div>
                ) : result ? (
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between gap-3 px-0.5 text-xs text-muted" aria-live="polite">
                            <span>
                                {totalMatches} {totalMatches === 1 ? "task" : "tasks"} found
                            </span>
                            {indexing ? <span>Updating message index…</span> : null}
                        </div>
                        {visibleComputers.map((computer) => (
                            <section key={computer.nodeId} className="min-w-0" aria-label={`${computer.computerName} search results`}>
                                <div className="flex min-h-9 items-center justify-between gap-3 border border-border bg-base-200/65 px-3 text-xs">
                                    <span className="flex min-w-0 items-center gap-2">
                                        <Monitor className="h-3.5 w-3.5 flex-none text-muted" aria-hidden="true" />
                                        <span className="truncate font-medium text-base-content">{computer.computerName}</span>
                                    </span>
                                    <span className="flex-none text-muted">
                                        {computer.error
                                            ? "Offline"
                                            : computer.refresh?.state === "indexing"
                                              ? `Indexing ${computer.refresh.succeeded}/${computer.refresh.requested}`
                                              : `${computer.matches.length} ${computer.matches.length === 1 ? "result" : "results"}`}
                                    </span>
                                </div>
                                {computer.error ? (
                                    <div className="border-x border-b border-error/30 bg-error/10 px-3 py-2 text-xs text-error">{computer.error}</div>
                                ) : null}
                                {computer.warning ? (
                                    <div className="border-x border-b border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">{computer.warning}</div>
                                ) : null}
                                <div className="mt-2 grid gap-2">
                                    {computer.matches.map((match) => (
                                        <SearchResultCard
                                            key={`${match.workspaceId}:${match.taskId}`}
                                            match={match}
                                            query={query}
                                            onOpen={() => void openMatch(computer.nodeId, match)}
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}
                        {totalMatches === 0 && !indexing ? (
                            <div className="flex min-h-48 items-center justify-center px-4 text-center">
                                <div>
                                    <SearchX className="mx-auto h-7 w-7 text-muted/60" aria-hidden="true" />
                                    <div className="mt-3 text-sm font-medium text-base-content">No matching tasks</div>
                                    <p className="mt-1 text-xs text-muted">
                                        {harnessFilter === "all" ? "Try a shorter phrase or different words." : "Try another harness or different words."}
                                    </p>
                                </div>
                            </div>
                        ) : null}
                        {result.failed > 0 ? (
                            <div className="border border-border bg-base-200/40 px-3 py-2 text-xs text-muted">
                                Searched {result.succeeded} of {result.requested} computers; {result.failed} unreachable.
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className="flex flex-1 items-center justify-center text-sm text-muted">Searching…</div>
                )}
            </div>
        </Modal>
    )
})
